<script setup lang="tsx">
import { computed, onMounted, ref, watch } from 'vue'
import useRiskColumns from './columns'
import columnStore from '@/stores/column'
import LoadingContainer from '../widgets/LoadingContainer.vue'
import http from '@/services/http'
import type { PageRequest, PageResponse, Response, Responsible, Risk, RiskCatalogMeta, SaveRisksRequest } from '@/models/api-generated'
import type { RiskFilter } from '@/models/filters-generated'
import toastStore from '@/stores/toast'
import Pagination from '../widgets/PaginationComponent.vue'
import RiskFilters from './RiskFilters.vue'
import RiskToolbar from './RiskToolbar.vue'
import { cloneDeep } from 'lodash'
import RiskTable from './RiskTable.vue'
import { useI18n } from 'vue-i18n'
import { Mode } from '@/types/utils'
import sliderStore from '@/stores/slider'
import { refreshThreatCallback } from './threat/threatShared'
import { refreshVulnerabilityCallback } from './vulnerability/vulnerabilityShared'
import { refreshOverviewCallback } from './overview/overviewShared'
import { getRiskConfig, riskConfig } from './riskConfig'
import Overview from './overview/Overview.vue'
import { watchScrollFocus, scrollToView } from '../methods/scroll'
import ReportGenerate from '../pdf/ReportGenerate.vue'
import { defaultRisk } from './shared'
import { DueDateOverdue } from '@/models/enums-generated'

const slider = sliderStore()
const props = defineProps({
	businessProcess: { default: 0 },
	component: { default: 'risks' },
	matrixSection: { type: Object as () => HTMLElement },
	riskScoreSection: { type: Object as () => HTMLElement },
})

const emit = defineEmits<{
	(e: 'switchToMatrixTab'): void
	(e: 'restoreTab'): void
}>()

const toaster = toastStore()
const { t } = useI18n()
const originalRisks = ref<Risk[]>([])
const model = ref<PageResponse<Risk>>()
const mode = ref<Mode>(Mode.Readonly)
const isEdit = computed(() => mode.value === Mode.Edit)
const isLoading = ref(false)
const isSaving = ref(false)
const { columns } = useRiskColumns(props.component)

const overviewRef = ref<InstanceType<typeof Overview>>()
const overviewSection = ref<HTMLElement>()
const reportGenerateRef = ref<InstanceType<typeof ReportGenerate>>()

const root = columnStore().defineComponent(props.component, fetch)
const riskFiltersRef = ref<InstanceType<typeof RiskFilters>>()

function handleSwitchToMatrix() {
	emit('switchToMatrixTab')
}

function handleRestoreTab() {
	emit('restoreTab')
}

function handleSetOverviewExpanded(value: boolean) {
	if (overviewRef.value) {
		overviewRef.value.expanded = value
	}
}

async function fetch(signal: AbortSignal) {
	const expandedRisks = new Set<number>()
	const expandedMetaIds = new Map<number, Set<number>>()

	for (const risk of model.value?.items ?? []) {
		if (risk.isExpanded) expandedRisks.add(risk.id)

		const ids = new Set<number>(risk.riskMetas.filter((m) => m.isExpanded).map((m) => m.id))

		if (ids.size) expandedMetaIds.set(risk.id, ids)
	}

	let url = 'api/risk/list'
	if (props.businessProcess) url = `api/bp/${props.businessProcess}/risks`

	const request: PageRequest<RiskFilter> = {
		page: root.pagination.page,
		limit: root.pagination.limit,
		filter: root.filterValue() as RiskFilter,
		orderBy: root.orderValue(),
	}

	isLoading.value = true
	const { data } = await http.post<Response<PageResponse<Risk>>>(url, request, { signal })
	if (!data.data) return

	model.value = data.data
	isLoading.value = false
	originalRisks.value = cloneDeep(data.data.items)

	for (const risk of model.value.items) {
		if (expandedRisks.has(risk.id)) risk.isExpanded = true

		const savedMetas = expandedMetaIds.get(risk.id)
		if (!savedMetas) continue

		for (const meta of risk.riskMetas) {
			if (savedMetas.has(meta.id ?? -1)) meta.isExpanded = true
		}
	}
}

const added = computed(() => model.value?.items.filter((i) => !i.id))
const changed = ref(new Set<number>())
const deletedRisks = ref(new Set<number>())
const deletedMetas = ref(new Set<number>())
const numChanges = computed(() => changed.value.size + deletedMetas.value.size + (added.value?.length ?? 0))

const validRisks = (rows: Risk[]) =>
	rows.every((r) => r.code.length && r.name.length && r.description.length && r.riskMetas.every((i) => i.catalogMeta.id))

const canSave = computed(() => validRisks(model.value?.items ?? []) && numChanges.value > 0)

const deletedMeasures = computed(
	() =>
		model.value?.items.flatMap((risk) => risk.riskMetas.flatMap((meta) => meta.mitigationSummary?.flatMap((s) => s.deletedIds ?? []))) ??
		[],
)

function trackRisk(risk: Risk) {
	watch(
		() => [
			risk.code,
			risk.name,
			risk.description,
			risk.riskMetas.map((m) => [
				m.probability,
				m.impact,
				m.treatment,
				m.catalogMeta.id,
				m.category?.id,
				m.mitigationSummary,
				m.impactedAssets,
				m.comment,
				m.manualResidual,
				m.acceptedResidualRisk,
				m.availability,
				m.confidentiality,
				m.documentReferences,
				m.dueDate,
				m.responsible,
				m.integrity,
				m.lastReview,
				m.treatment,
				m.urlReferences,
				m.vulnerabilities,
				m.threats,
			]),
		],
		() => markChanged(risk.id),
		{ deep: true },
	)
}

onMounted(() => {
	watch(
		() => model.value?.items,
		(items) => {
			if (!items) return
			items.forEach(trackRisk)
		},
	)
})

function markChanged(id: number) {
	if (!isEdit.value && mode.value !== Mode.Add) return
	if (id !== 0 && !deletedRisks.value.has(id)) {
		changed.value.add(id)
	}
}

function rowClass(i: number) {
	return {
		'is-changed':
			model.value?.items[i].id === 0 ||
			(changed.value.has(model.value?.items[i].id ?? 0) && !deletedRisks.value.has(model.value?.items[i].id ?? 0)),
		'is-deleted': deletedRisks.value.has(model.value?.items[i].id ?? 0),
		'text-danger': deletedRisks.value.has(model.value?.items[i].id ?? 0),
	}
}

function addRisk() {
	if (!model.value) return
	mode.value = Mode.Add
	model.value.items.unshift(defaultRisk())
}

function editRisk() {
	mode.value = Mode.Edit
}

async function save() {
	if (!model.value) return

	if (deletedMetas.value.size > 0) {
		return slider.requestDeleteConfirm(t('risk.confirmDelete'), 'risk', async () => {
			await doSave()
		})
	}

	await doSave()
}

async function doSave() {
	isSaving.value = true
	if (!model.value) return
	for (const r of model.value.items) {
		if (!r.catalog?.id && r.riskMetas[0]?.catalogMeta?.catalog?.id) {
			const c = r.riskMetas[0].catalogMeta.catalog
			r.catalog = { id: c.id, name: c.name, isCustom: c.isCustom }
		}
	}

	const request: SaveRisksRequest = {
		added: added.value ?? [],
		changed: model.value.items.filter((x) => changed.value.has(x.id)),
		deletedRiskMetas: [...deletedMetas.value],
		deletedMeasures: deletedMeasures.value,
	}

	const { status } = await http.post(`api/risk/save`, request)
	isSaving.value = false

	if (status === 200) {
		changed.value.clear()
		deletedRisks.value.clear()
		deletedMetas.value.clear()
		root.triggerFetch()

		if (mode.value === Mode.Add) {
			toaster.showSuccess(t('misc.successfullyAdded'))
		} else if (mode.value === Mode.Edit) {
			toaster.showSuccess(t('misc.successfullyEdited'))
		}
		refreshOverviewCallback.value?.()
		refreshThreatCallback.value?.()
		refreshVulnerabilityCallback.value?.()
		mode.value = Mode.Readonly
	}
}

async function reset() {
	mode.value = Mode.Readonly
	changed.value.clear()
	deletedRisks.value.clear()
	deletedMetas.value.clear()
	if (model.value) model.value.items = cloneDeep(originalRisks.value)
}

async function filterRisks(names?: string[], treatment?: string[], catalogs?: RiskCatalogMeta[], responsible?: Responsible) {
	if (names) columns.name.value.filter = names
	if (treatment) columns.treatment.value.filter = treatment
	if (catalogs) riskFiltersRef.value?.catalogFilterRef?.setCatalogs(catalogs ?? [])
	if (responsible) {
		columns.responsible.value.filter = [{ id: responsible.id, type: responsible.type }]
		columns.dueDate.value.filter = [DueDateOverdue]
	}
	scrollToView()
}

async function handleExportPdf() {
	reportGenerateRef.value?.openModal()
}

onMounted(async () => {
	watchScrollFocus(isEdit)
	if (props.businessProcess) riskConfig.value = await getRiskConfig()
})

defineExpose({
	addRisk,
	editRisk,
	fetchRisks: root.triggerFetch,
	filterRisks,
	handleExportPdf,
})
</script>

<template>
	<div ref="overviewSection">
		<Overview v-if="!props.businessProcess" ref="overviewRef" @go-to-risks="filterRisks" :risk-config="riskConfig" :is-dashboard="false" />
	</div>

	<RiskFilters ref="riskFiltersRef"></RiskFilters>

	<LoadingContainer v-model="isLoading" class="rounded-container mt-3 p-3">
		<template v-if="model">
			<RiskTable
				:component="props.component"
				v-model:items="model.items"
				v-model:deleted-risk-ids="deletedRisks"
				v-model:deleted-meta-ids="deletedMetas"
				:is-edit="isEdit"
				:row-class="rowClass"
				:business-process="props.businessProcess"
				ref="riskTableRef"
			>
			</RiskTable
		></template>
	</LoadingContainer>
	<Pagination
		v-show="!isEdit"
		v-model="root.pagination"
		:total-pages="model?.pages"
		:tableTitle="props.businessProcess ? 'bp_risks' : 'risks'"
	/>

	<div v-if="[Mode.Add, Mode.Edit].includes(mode)" class="pb-5"></div>
	<RiskToolbar
		v-if="[Mode.Add, Mode.Edit].includes(mode)"
		:can-save="canSave"
		:is-saving="isSaving"
		:num-changes="numChanges"
		@reset="reset"
		@save="save"
	/>

	<ReportGenerate
		ref="reportGenerateRef"
		:overview-ref="overviewRef"
		:overview-section="overviewSection"
		:matrix-section="props.matrixSection"
		:risk-score-section="props.riskScoreSection"
		:filter-value="root.filterValue"
		:order-value="root.orderValue"
		@switch-to-matrix-tab="handleSwitchToMatrix"
		@restore-tab="handleRestoreTab"
		@set-overview-expanded="handleSetOverviewExpanded"
	/>
</template>

<style lang="sass" scoped>
@import '~bootstrap/scss/functions'
@import '~bootstrap/scss/variables'

:deep(.odd td)
  color: var(--bs-body-color)
  background-color: var(--bs-secondary-bg)
</style>
